import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { apiLogin, apiRegister, apiSetPassword, apiMe, apiAcceptInvite } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, obj = user
  const [ready, setReady] = useState(false);

  const loadMe = useCallback(async () => {
    const token = localStorage.getItem("cv_token");
    if (!token) {
      setUser(false);
      setReady(true);
      return;
    }
    try {
      const data = await apiMe();
      setUser(data.user);
    } catch (e) {
      localStorage.removeItem("cv_token");
      setUser(false);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  const persist = (data) => {
    localStorage.setItem("cv_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const login = async (email, password) => persist(await apiLogin({ email, password }));
  const register = async (email, password, name) =>
    persist(await apiRegister({ email, password, name }));
  const setPassword = async (email, password, session_id) =>
    persist(await apiSetPassword({ email, password, session_id }));
  const acceptInvite = async (token, password) =>
    persist(await apiAcceptInvite({ token, password }));

  const logout = () => {
    localStorage.removeItem("cv_token");
    setUser(false);
  };

  const isAdmin = user && (user.role === "admin" || user.role === "owner");

  return (
    <AuthContext.Provider
      value={{ user, ready, login, register, setPassword, acceptInvite, logout, isAdmin, refresh: loadMe }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
